import React, { useEffect, useMemo, useState, useCallback } from 'react';
import {
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Grid,
  Typography,
  Alert,
  Avatar
} from '@mui/material';
import {
  Description,
  Quiz,
  Assignment,
  Leaderboard,
  CalendarToday,
  School,
  ArrowBack,
  EventNote,
  HistoryEdu,
  MenuBook,
  Bookmark
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import api from '../api';
import { useAuth } from '../contexts/AuthContext';
import { useClearCacheOnLogout } from '../hooks/useClearCacheOnLogout';
import Spinner from '../components/Spinner';

interface MaterialItem {
  _id: string;
  title: string;
  description: string;
  subject: { _id: string; name: string };
  className: string;
  fileType: string;
  fileUrl: string;
  fileName: string;
  isVisible: boolean;
  staff: { name: string };
  createdAt: string;
}

interface QuizQuestion {
  _id: string;
  questionText: string;
  questionType: 'multiple-choice' | 'short-answer' | 'essay';
  options?: string[];
  correctAnswerIndex?: number;
  correctAnswer?: string;
  points?: number;
}

interface QuizItem {
  _id: string;
  title: string;
  description: string;
  instructions?: string;
  durationMinutes?: number;
  attemptsAllowed?: number;
  subject: { _id: string; name: string };
  className: string;
  questions: QuizQuestion[];
  totalPoints: number;
  dueDate?: string;
  isPublished: boolean;
  selectedStudents?: string[];
}

interface AssignmentItem {
  _id: string;
  title: string;
  description: string;
  subject: { _id: string; name: string };
  className: string;
  instructions: string;
  totalPoints: number;
  dueDate: string;
  attachmentUrl: string;
  attachmentName: string;
  isPublished: boolean;
}

interface GradeItem {
  _id: string;
  assignment: AssignmentItem;
  score?: number;
  maxScore?: number;
  status: string;
  feedback: string;
  gradedAt?: string;
  submittedAt?: string;
}

interface AttendanceRecord {
  _id: string;
  attendanceDate: string;
  status: string;
  className: string;
  remarks: string;
  staff: { name: string };
}

interface TermSettings {
  schoolName?: string;
  activeTermName?: string;
  termDurationMonths?: number;
  activeTermStartDate?: string;
  activeTermEndDate?: string;
  termEndDate?: string;
  marksSubmissionDeadline?: string;
  logoUrl?: string;
  logoUpload?: {
    contentType?: string;
    data?: string | { type?: string; data?: number[] };
  };
}

const sectionConfig = [
  {
    key: 'materials',
    title: 'Materials',
    description: 'Download and review teacher-uploaded study materials for your class.',
    icon: <Description sx={{ fontSize: 40, color: '#1e88e5' }} />
  },
  {
    key: 'quizzes',
    title: 'Quizzes',
    description: 'View quizzes published by your teachers and keep track of your practice.',
    icon: <Quiz sx={{ fontSize: 40, color: '#43a047' }} />
  },
  {
    key: 'assignments',
    title: 'Assignments',
    description: 'Check tasks assigned by your teachers and access submission resources.',
    icon: <Assignment sx={{ fontSize: 40, color: '#f4511e' }} />
  },
  {
    key: 'grades',
    title: 'Grades/Marks',
    description: 'See your graded work, feedback, and score details in one place.',
    icon: <Leaderboard sx={{ fontSize: 40, color: '#6a1b9a' }} />
  },
  {
    key: 'attendance',
    title: 'Attendance',
    description: 'Review your attendance record as marked by your teachers.',
    icon: <CalendarToday sx={{ fontSize: 40, color: '#fb8c00' }} />
  },
  {
    key: 'exam-ticket',
    title: 'Exam Ticket',
    description: 'Access your exam/hall ticket and eligibility details for the active term.',
    icon: <EventNote sx={{ fontSize: 40, color: '#0288d1' }} />
  },
  {
    key: 'term-history',
    title: 'Term History',
    description: 'Review completed term submissions and historical performance records.',
    icon: <HistoryEdu sx={{ fontSize: 40, color: '#6d4c41' }} />
  },
  {
    key: 'report-card',
    title: 'Report Card',
    description: 'View and download your report card for the active term.',
    icon: <MenuBook sx={{ fontSize: 40, color: '#1976d2' }} />
  },
  {
    key: 'bookmarks',
    title: 'Bookmarks',
    description: 'View your saved materials, quizzes, and assignments in one place.',
    icon: <Bookmark sx={{ fontSize: 40, color: '#f59e0b' }} />
  }
];




const SchoolWork: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const filteredSectionConfig = useMemo(() => {
    return sectionConfig.filter(section => {
      if (section.key === 'report-card') {
        return user?.reportCardPublished === true;
      }
      if (section.key === 'exam-ticket') {
        return user?.examTicketPublished === true;
      }
      if (section.key === 'bookmarks') {
        return true;
      }
      return true;
    });
  }, [user]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [materials, setMaterials] = useState<MaterialItem[]>([]);
  const [quizzes, setQuizzes] = useState<QuizItem[]>([]);
  const [assignments, setAssignments] = useState<AssignmentItem[]>([]);
  const [grades, setGrades] = useState<GradeItem[]>([]);
  const [attendance, setAttendance] = useState<AttendanceRecord[]>([]);
  const [termSettings, setTermSettings] = useState<TermSettings | null>(null);

  // Clear cache when user logs out or switches accounts
  const clearSchoolWorkCache = useCallback(() => {
    setMaterials([]);
    setQuizzes([]);
    setAssignments([]);
    setGrades([]);
    setAttendance([]);
    setError(null);
    setLoading(true);
  }, []);

  useClearCacheOnLogout(clearSchoolWorkCache);

  const getLogoSrc = (logoUrl?: string, logoUpload?: { contentType?: string; data?: string | { type?: string; data?: number[] } }) => {
    if (logoUrl) return logoUrl;
    if (!logoUpload?.contentType || !logoUpload.data) return '';
    if (typeof logoUpload.data === 'string') {
      return `data:${logoUpload.contentType};base64,${logoUpload.data}`;
    }
    if (typeof logoUpload.data === 'object' && Array.isArray(logoUpload.data.data)) {
      try {
        const bytes = Uint8Array.from(logoUpload.data.data);
        let binary = '';
        for (let i = 0; i < bytes.length; i += 1) {
          binary += String.fromCharCode(bytes[i]);
        }
        return `data:${logoUpload.contentType};base64,${window.btoa(binary)}`;
      } catch {
        return '';
      }
    }
    return '';
  };

  const isHighSchoolEducation = (educationLevel?: string) =>
    typeof educationLevel === 'string' && /high[- ]school/i.test(educationLevel);

  const hasSchoolWorkAccess = useMemo(() => {
    return Boolean(
      isHighSchoolEducation(user?.educationLevel) &&
      (user?.school || user?.schoolName || user?.schoolEmail)
    );
  }, [user]);

  useEffect(() => {
    const loadSchoolWork = async () => {
      setLoading(true);
      setError(null);
      setMaterials([]);
      setQuizzes([]);
      setAssignments([]);
      setGrades([]);
      setAttendance([]);
      try {
        const [materialsRes, quizzesRes, assignmentsRes, gradesRes, attendanceRes, settingsRes] = await Promise.all([
          api.get('/school-work/materials'),
          api.get('/school-work/quizzes'),
          api.get('/school-work/assignments'),
          api.get('/school-work/grades'),
          api.get('/school-work/attendance'),
          api.get('/school-work/settings')
        ]);
        setMaterials(Array.isArray(materialsRes.data) ? materialsRes.data : []);
        setQuizzes(Array.isArray(quizzesRes.data) ? quizzesRes.data : []);
        setAssignments(Array.isArray(assignmentsRes.data) ? assignmentsRes.data : []);
        setGrades(Array.isArray(gradesRes.data) ? gradesRes.data : []);
        setAttendance(Array.isArray(attendanceRes.data) ? attendanceRes.data : []);
        setTermSettings(settingsRes.data || null);
      } catch (err: any) {
        setError(err.response?.data?.message || 'Unable to load school work content');
      } finally {
        setLoading(false);
      }
    };
    if (hasSchoolWorkAccess) {
      loadSchoolWork();
    } else {
      setLoading(false);
    }
  }, [hasSchoolWorkAccess, user?._id]);

  const counts = useMemo(() => ({
    materials: materials.length,
    quizzes: quizzes.length,
    assignments: assignments.length,
    grades: grades.length,
    attendance: attendance.length,
    'exam-ticket': 1,
    'term-history': 1,
    'report-card': 1
  }), [materials, quizzes, assignments, grades, attendance]);



  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4" gutterBottom>School Work Dashboard</Typography>
          <Typography color="text.secondary">Access materials, quizzes, assignments, grades, and attendance shared by your school teachers.</Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          <Button variant="outlined" startIcon={<ArrowBack />} onClick={() => navigate('/')}>Back to Home Page</Button>
          <Button variant="contained" startIcon={<School />} onClick={() => navigate(0)}>
            Refresh Page
          </Button>
        </Box>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <Spinner />
        </Box>
      ) : !hasSchoolWorkAccess ? (
        <Alert severity="info" sx={{ mb: 4 }}>
          School Work is available only to high school students registered under a registered school.
          Please ensure your school is selected in your profile and refresh the page.
        </Alert>
      ) : error ? (
        <Alert severity="error" sx={{ mb: 4 }}>{error}</Alert>
      ) : (
        <>
          {termSettings && (
            <Box sx={{ mb: 4, p: 3, borderRadius: 2, backgroundColor: 'background.paper', boxShadow: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
                <Box>
                  <Typography variant="h6" sx={{ mb: 1 }}>Current Term Details</Typography>
                  <Typography color="text.secondary">{termSettings.activeTermName || 'Active Term'}</Typography>
                  {termSettings.schoolName && (
                    <Typography color="text.secondary" sx={{ mt: 1 }}>School: {termSettings.schoolName}</Typography>
                  )}
                  {user?.registrationId && (
                    <Typography color="text.secondary" sx={{ mt: 1 }}>Student ID: {user.registrationId}</Typography>
                  )}
                </Box>
                {getLogoSrc(termSettings.logoUrl, termSettings.logoUpload) && (
                  <Avatar
                    src={getLogoSrc(termSettings.logoUrl, termSettings.logoUpload)}
                    alt={termSettings.schoolName || 'School Logo'}
                    sx={{ width: 72, height: 72, border: '1px solid', borderColor: 'divider' }}
                  />
                )}
              </Box>
              <Typography sx={{ mt: 2 }}>
                Duration: {termSettings.termDurationMonths ? `${termSettings.termDurationMonths} month${termSettings.termDurationMonths === 1 ? '' : 's'}` : 'Not configured'}
              </Typography>
              {termSettings.activeTermStartDate && (
                <Typography>
                  Term begins: {new Date(termSettings.activeTermStartDate).toLocaleDateString()}
                </Typography>
              )}
              {termSettings.termEndDate && (
                <Typography>
                  Term ends: {new Date(termSettings.termEndDate).toLocaleDateString()}
                </Typography>
              )}
            </Box>
          )}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            {filteredSectionConfig.map((section) => (
              <Grid item xs={12} sm={6} md={4} key={section.key}>
              <Card
                onClick={() => navigate(`/school-work/${section.key}`)}
                sx={{ cursor: 'pointer', height: '100%', display: 'flex', flexDirection: 'column', ':hover': { boxShadow: 6, transform: 'translateY(-2px)' }, transition: 'all 0.2s ease-in-out' }}
              >
                <CardContent sx={{ flexGrow: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    {section.icon}
                    <Box>
                      <Typography variant="h6">{section.title}</Typography>
                      <Typography variant="body2" color="text.secondary">{section.description}</Typography>
                    </Box>
                  </Box>
                  <Typography variant="h3" sx={{ fontWeight: 700, mt: 2, color: 'primary.main' }}>
                    {counts[section.key as keyof typeof counts]}
                  </Typography>
                </CardContent>
                <CardActions>
                  <Button fullWidth variant="text" size="large" onClick={(e) => { e.stopPropagation(); navigate(`/school-work/${section.key}`); }}>
                    View {section.title}
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
        </>
      )}
    </Box>
  );
};

export default SchoolWork;
